import { IkeToken, IkeUserRole } from '@ikegps/core-api';
import { MaterialIcon } from '../components/icon/icons';
import { IMithrilStateComponent } from '../components/mithril.component';
import { T } from '../i18n';

export enum IkePageName {
	// Internal Pages
	root = 'root',
	login = 'auth.login',
	logout = 'auth.logout',
	map = 'map',
	jobList = 'job.list',
	formList = 'form.list',
	formNew = 'form.new',
	formEdit = 'form.edit',
	accountList = 'account.list',
	collectionImport = 'collection.import',
	collectionMeasure = 'collection.measure',
	collectionReport = 'collection.report',
	collectionHistory = 'collection.history',
	forgotPassword = 'forgot.password',
	pageMissing = 'status.404',
	orgSearch = 'org.search',
	dashboardPolestatus = 'dashboard.polestatus',
	dashboardPoleloading = 'dashboard.poleloading',
	dashboardMenu = 'dashboard.menu',
	topologicalEditor = 'topological.editor',
	userSSOMigrate = 'user.sso.migrate',

	// External Pages
	genericExternal = 'generic.external',
	comedEsri = 'comed.esri',
}

export interface IkePageBaseConfig {
	name: IkePageName;

	/** Authorization settings */
	auth: {
		/** Whether page requires a logged-in user. */
		required: boolean;
		/** Tokens required to view page. */
		tokens?: IkeToken[];
		/** User role required to view page */
		userLevel?: IkeUserRole[];
		/** A black list of user types that cant access this page */
		userLevelBlackList?: IkeUserRole[];
	};

	/** Layout settings */
	layout?: {
		/** Whether to show a fixed banner (defaults to true). */
		banner?: boolean;
		/** Whether to show a fixed nav-drawer (defaults to true). */
		nav?: boolean;
	};

	/** Navigation settings */
	nav?: {
		/** List route with name. */
		name: T;
		/** List icon*/
		icon: MaterialIcon;
	};
}

export interface IkePageConfig extends IkePageBaseConfig {
	type: 'internal';

	/** Relative path.
	 * @see https://mithril.js.org/route.html#routing-parameters
	 */
	path: string | string[];

	/** Whether the route supports changing department (defaults to true). */
	supportDepartmentChange?: boolean;
}

export interface IkeExternalLinkConfig extends IkePageBaseConfig {
	type: 'external';

	/**
	 * The `https://` link
	 */
	path: string;
}

export type IkeLinkConfig = IkeExternalLinkConfig | IkePageConfig;

export abstract class IkePage extends IMithrilStateComponent {
	config: IkePageConfig;

	constructor(config: IkePageConfig) {
		super();
		this.config = config;
		this.setConfigDefaults();
	}

	private setConfigDefaults() {
		this.config.layout = {
			banner: true,
			nav: true,
			...this.config.layout,
		};

		if (this.config.supportDepartmentChange == null) {
			this.config.supportDepartmentChange = false;
		}
	}

	/**
	 * This will be called before the loading of the next page.
	 */
	unload(): void {
		this.onBeforeUnload();
		this.onRemove();
	}

	/**
	 * This _may_ be implemented by any IkePage to handle their own unloading function.
	 * For example, removing any edit state that the page is storing.
	 */
	protected onBeforeUnload(): void {
		return;
	}
}
